import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton, Chip,
  Divider, Card, CardContent, Alert, CircularProgress, Tooltip,
  Tabs, Tab, FormControlLabel, Switch
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, Print as PrintIcon,
  Visibility as VisibilityIcon, Business as BusinessIcon,
  Receipt as ReceiptIcon, People as PeopleIcon
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

const Sales = () => {
  const [tabValue, setTabValue] = useState(0);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerFormData, setCustomerFormData] = useState({
    name: '', contact_person: '', phone: '', email: '', address: '',
    gst_number: '', is_active: true
  });

  const [products, setProducts] = useState([]);
  const [isInterState, setIsInterState] = useState(false);

  const [formData, setFormData] = useState({
    customer: '',
    so_number: '',
    delivery_date: '',
    status: 'draft',
    notes: '',
    items: []
  });

  const ORDER_STATUSES = [
    { value: 'draft', label: 'Draft' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'dispatched', label: 'Dispatched' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchOrders(), fetchCustomers(), fetchProducts()]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get('/sales/orders/');
      const data = Array.isArray(response.data) ? response.data
        : (response.data?.results || []);
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch sales orders');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/sales/customers/');
      const data = Array.isArray(response.data) ? response.data
        : (response.data?.results || []);
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/inventory/products/');
      const data = Array.isArray(response.data) ? response.data
        : (response.data?.results || []);
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // ========== CUSTOMER FUNCTIONS ==========

  const handleOpenCustomerDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setCustomerFormData({
        name: customer.name || '',
        contact_person: customer.contact_person || '',
        phone: customer.phone || '',
        email: customer.email || '',
        address: customer.address || '',
        gst_number: customer.gst_number || '',
        is_active: customer.is_active !== undefined ? customer.is_active : true
      });
    } else {
      setEditingCustomer(null);
      setCustomerFormData({
        name: '', contact_person: '', phone: '', email: '',
        address: '', gst_number: '', is_active: true
      });
    }
    setOpenCustomerDialog(true);
  };

  const handleCloseCustomerDialog = () => {
    setOpenCustomerDialog(false);
    setEditingCustomer(null);
  };

  const handleCustomerChange = (field) => (event) => {
    setCustomerFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleCustomerSubmit = async () => {
    try {
      if (!customerFormData.name) return toast.error('Customer name is required');
      if (!customerFormData.phone) return toast.error('Phone number is required');

      if (editingCustomer) {
        await api.put(`/sales/customers/${editingCustomer.id}/`, customerFormData);
        toast.success('Customer updated successfully');
      } else {
        await api.post('/sales/customers/', customerFormData);
        toast.success('Customer created successfully');
      }
      handleCloseCustomerDialog();
      fetchCustomers();
    } catch (error) {
      console.error('Error saving customer:', error);
      const msg = error.response?.data
        ? JSON.stringify(error.response.data)
        : 'Failed to save customer';
      toast.error(msg);
    }
  };

  const handleCustomerDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await api.delete(`/sales/customers/${id}/`);
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  // ========== ORDER FUNCTIONS ==========

  const getProductUOM = (product) => {
    if (!product) return 'N/A';
    if (product.uom_symbol) return product.uom_symbol;
    if (product.uom && typeof product.uom === 'object') {
      return product.uom.symbol || product.uom.name || 'N/A';
    }
    if (product.uom_details?.symbol) return product.uom_details.symbol;
    return 'N/A';
  };

  const createEmptyItem = (interState = false) => ({
    product: '',
    product_name: '',
    uom: '',
    quantity: 0,
    unit_price: 0,
    gst_rate: 0,
    cgst_amount: 0,
    sgst_amount: 0,
    igst_amount: 0,
    total: 0,
    is_inter_state: interState
  });

  const handleOpenDialog = (order = null) => {
    if (order) {
      setEditingOrder(order);
      setIsInterState(order.is_inter_state || false);
      setFormData({
        customer: order.customer?.id || order.customer || '',
        so_number: order.so_number || '',
        delivery_date: order.delivery_date || '',
        status: order.status || 'draft',
        notes: order.notes || '',
        items: (order.items || []).map(i => ({
          product: i.product?.id || i.product || '',
          product_name: i.product_details?.name || i.product_name || '',
          uom: i.product_details?.uom_symbol || i.uom || '',
          quantity: i.quantity || 0,
          unit_price: i.unit_price || 0,
          gst_rate: i.gst_rate || 0,
          cgst_amount: i.cgst_amount || 0,
          sgst_amount: i.sgst_amount || 0,
          igst_amount: i.igst_amount || 0,
          total: i.total || 0,
          is_inter_state: i.is_inter_state || false
        }))
      });
    } else {
      setEditingOrder(null);
      setIsInterState(false);
      setFormData({
        customer: '',
        so_number: '',
        delivery_date: '',
        status: 'draft',
        notes: '',
        items: [createEmptyItem(false)]
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOrder(null);
    setIsInterState(false);
  };

  const handleChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleInterStateToggle = (event) => {
    const newValue = event.target.checked;
    setIsInterState(newValue);
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        calculateItemTotals({ ...item, is_inter_state: newValue })
      )
    }));
    toast.success(`GST Mode: ${newValue ? 'Inter-State (IGST)' : 'Intra-State (CGST + SGST)'}`);
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };

      if (field === 'product') {
        const selected = products.find(p => p.id === parseInt(value));
        if (selected) {
          newItems[index].product_name = selected.name;
          newItems[index].unit_price = parseFloat(selected.selling_price) || 0;
          newItems[index].gst_rate = parseFloat(selected.gst_rate) || 0;
          newItems[index].uom = getProductUOM(selected);
        }
      }
      newItems[index] = calculateItemTotals(newItems[index]);
      return { ...prev, items: newItems };
    });
  };

  const calculateItemTotals = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const gstRate = parseFloat(item.gst_rate) || 0;
    const isItemInterState = item.is_inter_state || false;

    const subtotal = quantity * unitPrice;
    let cgst = 0, sgst = 0, igst = 0;

    if (isItemInterState) {
      igst = subtotal * (gstRate / 100);
    } else {
      cgst = subtotal * (gstRate / 200);
      sgst = subtotal * (gstRate / 200);
    }
    const total = subtotal + cgst + sgst + igst;

    return { ...item, cgst_amount: cgst, sgst_amount: sgst, igst_amount: igst, total };
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, createEmptyItem(isInterState)]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    let subtotal = 0, totalCgst = 0, totalSgst = 0, totalIgst = 0;
    formData.items.forEach(item => {
      subtotal += (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      totalCgst += parseFloat(item.cgst_amount) || 0;
      totalSgst += parseFloat(item.sgst_amount) || 0;
      totalIgst += parseFloat(item.igst_amount) || 0;
    });
    const totalTax = totalCgst + totalSgst + totalIgst;
    return { subtotal, totalCgst, totalSgst, totalIgst, totalTax, netAmount: subtotal + totalTax };
  };

  const handleSubmit = async () => {
    try {
      if (!formData.customer) return toast.error('Please select a customer');
      if (!formData.items.length || !formData.items[0].product) {
        return toast.error('Please add at least one product');
      }

      const totals = calculateTotals();

      const submitData = {
        customer_id: parseInt(formData.customer),
        delivery_date: formData.delivery_date || null,
        status: formData.status,
        notes: formData.notes || '',
        is_inter_state: isInterState,
        total_amount: totals.subtotal,
        cgst_amount: totals.totalCgst,
        sgst_amount: totals.totalSgst,
        igst_amount: totals.totalIgst,
        tax_amount: totals.totalTax,
        net_amount: totals.netAmount,
        items: formData.items.map(item => ({
          product: parseInt(item.product),
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          gst_rate: parseFloat(item.gst_rate) || 0,
          cgst_amount: parseFloat(item.cgst_amount) || 0,
          sgst_amount: parseFloat(item.sgst_amount) || 0,
          igst_amount: parseFloat(item.igst_amount) || 0,
          is_inter_state: item.is_inter_state || false,
          total: parseFloat(item.total) || 0
        }))
      };

      if (editingOrder) {
        await api.put(`/sales/orders/${editingOrder.id}/`, submitData);
        toast.success('Sales order updated successfully');
      } else {
        await api.post('/sales/orders/', submitData);
        toast.success('Sales order created successfully');
      }
      handleCloseDialog();
      fetchOrders();
    } catch (error) {
      console.error('Error saving sales order:', error);
      const msg = error.response?.data
        ? JSON.stringify(error.response.data)
        : 'Failed to save sales order';
      toast.error(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sales order?')) return;
    try {
      await api.delete(`/sales/orders/${id}/`);
      toast.success('Sales order deleted successfully');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to delete sales order');
    }
  };

  const handleViewOrder = (order) => {
    setViewingOrder(order);
    setOpenViewDialog(true);
  };

  const getStatusColor = (status) => ({
    draft: 'default', confirmed: 'info', dispatched: 'primary',
    delivered: 'success', cancelled: 'error', pending: 'warning'
  }[status] || 'default');

  const totals = calculateTotals();

  const filteredCustomers = customers.filter(c => {
    if (!customerSearch) return true;
    const s = customerSearch.toLowerCase();
    return (
      c.name?.toLowerCase().includes(s) ||
      c.contact_person?.toLowerCase().includes(s) ||
      c.phone?.toLowerCase().includes(s) ||
      c.gst_number?.toLowerCase().includes(s)
    );
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold">Sales</Typography>
            <Typography variant="body2" color="textSecondary">
              Manage customers and sales orders
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {tabValue === 0 && (
              <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}>
                Create Sales Order
              </Button>
            )}
            {tabValue === 1 && (
              <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => handleOpenCustomerDialog()}>
                Add Customer
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<ReceiptIcon />} iconPosition="start" label="Sales Orders" />
          <Tab icon={<PeopleIcon />} iconPosition="start" label="Customers" />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>SO Number</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Order Date</TableCell>
                    <TableCell>Delivery Date</TableCell>
                    <TableCell>GST Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body1" color="textSecondary">
                          No sales orders found. Click "Create Sales Order" to add one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : orders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.so_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.customer?.name || 'N/A'}</TableCell>
                      <TableCell>
                        {order.order_date ? format(new Date(order.order_date), 'dd/MM/yyyy') : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {order.delivery_date ? format(new Date(order.delivery_date), 'dd/MM/yyyy') : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip label={order.is_inter_state ? 'IGST' : 'CGST+SGST'}
                          color={order.is_inter_state ? 'warning' : 'info'} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip label={(order.status || 'draft').toUpperCase()}
                          color={getStatusColor(order.status)} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        ₹{parseFloat(order.net_amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="info"
                          onClick={() => handleViewOrder(order)}>
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton size="small" color="primary"
                          onClick={() => handleOpenDialog(order)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error"
                          onClick={() => handleDelete(order.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <TextField fullWidth
                placeholder="Search customers by name, contact, phone, or GST..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)} />
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Contact Person</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>GST Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body1" color="textSecondary">
                          No customers found. Click "Add Customer" to add one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredCustomers.map((customer) => (
                    <TableRow key={customer.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {customer.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{customer.contact_person || 'N/A'}</TableCell>
                      <TableCell>{customer.phone || 'N/A'}</TableCell>
                      <TableCell>{customer.email || 'N/A'}</TableCell>
                      <TableCell>{customer.gst_number || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip label={customer.is_active ? 'Active' : 'Inactive'}
                          color={customer.is_active ? 'success' : 'default'} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary"
                          onClick={() => handleOpenCustomerDialog(customer)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error"
                          onClick={() => handleCustomerDelete(customer.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* CREATE/EDIT ORDER DIALOG */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {editingOrder ? 'Edit Sales Order' : 'Create Sales Order'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Customer</InputLabel>
                <Select value={formData.customer}
                  onChange={handleChange('customer')} label="Customer">
                  <MenuItem value="">Select Customer</MenuItem>
                  {customers.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="SO Number"
                value={formData.so_number}
                onChange={handleChange('so_number')}
                placeholder="Auto-generated if left blank" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Delivery Date" type="date"
                value={formData.delivery_date}
                onChange={handleChange('delivery_date')}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={formData.status}
                  onChange={handleChange('status')} label="Status">
                  {ORDER_STATUSES.map(s => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined"
                sx={{ p: 2, bgcolor: isInterState ? '#fff3e0' : '#e8f5e9' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">GST Type</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {isInterState
                        ? 'Inter-State (IGST) - Full GST rate applied'
                        : 'Intra-State (CGST + SGST) - Split equally'}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={<Switch checked={isInterState}
                      onChange={handleInterStateToggle}
                      color={isInterState ? 'warning' : 'primary'} />}
                    label={isInterState ? 'Inter-State (IGST)' : 'Intra-State (CGST+SGST)'} />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight="bold">Order Items</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addItem}>
                  Add Item
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ minWidth: 200 }}>Product</TableCell>
                      <TableCell style={{ minWidth: 80 }}>UOM</TableCell>
                      <TableCell style={{ minWidth: 100 }}>Qty</TableCell>
                      <TableCell style={{ minWidth: 120 }}>Unit Price (₹)</TableCell>
                      <TableCell style={{ minWidth: 100 }}>GST Rate</TableCell>
                      <TableCell style={{ minWidth: 80 }}>Total</TableCell>
                      <TableCell style={{ minWidth: 60 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.items.map((item, index) => {
                      const selected = products.find(p => p.id === parseInt(item.product));
                      const uomDisplay = selected ? getProductUOM(selected) : (item.uom || 'N/A');
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select value={item.product || ''}
                                onChange={(e) => handleItemChange(index, 'product', e.target.value)}
                                displayEmpty>
                                <MenuItem value="">Select Product</MenuItem>
                                {products.map(p => (
                                  <MenuItem key={p.id} value={p.id}>
                                    {p.name} ({p.code})
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <Chip label={uomDisplay} size="small"
                              color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <TextField fullWidth size="small" type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }} />
                          </TableCell>
                          <TableCell>
                            <TextField fullWidth size="small" type="number"
                              value={item.unit_price}
                              onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }} />
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select value={item.gst_rate || 0}
                                onChange={(e) => handleItemChange(index, 'gst_rate', e.target.value)}>
                                <MenuItem value={0}>0%</MenuItem>
                                <MenuItem value={5}>5%</MenuItem>
                                <MenuItem value={12}>12%</MenuItem>
                                <MenuItem value={18}>18%</MenuItem>
                                <MenuItem value={28}>28%</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              ₹{parseFloat(item.total || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="error"
                              onClick={() => removeItem(index)}
                              disabled={formData.items.length === 1}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined"
                sx={{ bgcolor: isInterState ? '#fff3e0' : '#e8f5e9' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    GST Calculation ({isInterState ? 'Inter-State - IGST' : 'Intra-State - CGST + SGST'})
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">Subtotal</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ₹{totals.subtotal.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">Total Tax</Typography>
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        ₹{totals.totalTax.toFixed(2)}
                      </Typography>
                    </Grid>
                    {!isInterState && (
                      <>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="textSecondary">CGST</Typography>
                          <Typography variant="body1">
                            ₹{totals.totalCgst.toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="textSecondary">SGST</Typography>
                          <Typography variant="body1">
                            ₹{totals.totalSgst.toFixed(2)}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {isInterState && (
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="textSecondary">IGST</Typography>
                        <Typography variant="body1" color="warning.main" fontWeight="bold">
                          ₹{totals.totalIgst.toFixed(2)}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={isInterState ? 9 : 6}>
                      <Typography variant="body2" color="textSecondary">
                        Net Amount (with GST)
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        ₹{totals.netAmount.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Notes" multiline rows={2}
                value={formData.notes}
                onChange={handleChange('notes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingOrder ? 'Update' : 'Create'} Sales Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* CUSTOMER DIALOG */}
      <Dialog open={openCustomerDialog} onClose={handleCloseCustomerDialog}
        maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {editingCustomer ? 'Edit Customer' : 'Add Customer'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Customer Name"
                value={customerFormData.name}
                onChange={handleCustomerChange('name')} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Contact Person"
                value={customerFormData.contact_person}
                onChange={handleCustomerChange('contact_person')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone"
                value={customerFormData.phone}
                onChange={handleCustomerChange('phone')} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" type="email"
                value={customerFormData.email}
                onChange={handleCustomerChange('email')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address" multiline rows={2}
                value={customerFormData.address}
                onChange={handleCustomerChange('address')} />
            </Grid>
            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="GST Number"
                value={customerFormData.gst_number}
                onChange={handleCustomerChange('gst_number')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={customerFormData.is_active}
                  onChange={(e) => setCustomerFormData(prev => ({
                    ...prev, is_active: e.target.checked
                  }))} />}
                label={customerFormData.is_active ? 'Active' : 'Inactive'} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomerDialog}>Cancel</Button>
          <Button onClick={handleCustomerSubmit} variant="contained">
            {editingCustomer ? 'Update Customer' : 'Create Customer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* VIEW DIALOG */}
      <Dialog open={openViewDialog}
        onClose={() => { setOpenViewDialog(false); setViewingOrder(null); }}
        maxWidth="lg" fullWidth>
        <DialogTitle>
          Sales Order Details
          {viewingOrder?.so_number ? ` - ${viewingOrder.so_number}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {!viewingOrder ? (
            <Alert severity="info">No sales order selected.</Alert>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">SO Number</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {viewingOrder.so_number || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Customer</Typography>
                  <Typography variant="body1">
                    {viewingOrder.customer?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Order Date</Typography>
                  <Typography variant="body1">
                    {viewingOrder.order_date
                      ? format(new Date(viewingOrder.order_date), 'dd/MM/yyyy') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Delivery Date</Typography>
                  <Typography variant="body1">
                    {viewingOrder.delivery_date
                      ? format(new Date(viewingOrder.delivery_date), 'dd/MM/yyyy') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={(viewingOrder.status || 'draft').toUpperCase()}
                      color={getStatusColor(viewingOrder.status)} size="small" />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">GST Type</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={viewingOrder.is_inter_state ? 'IGST' : 'CGST + SGST'}
                      color={viewingOrder.is_inter_state ? 'warning' : 'info'} size="small" />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Subtotal</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    ₹{parseFloat(viewingOrder.total_amount || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Net Amount</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    ₹{parseFloat(viewingOrder.net_amount || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Order Items
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>UOM</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">GST %</TableCell>
                      <TableCell align="right">CGST</TableCell>
                      <TableCell align="right">SGST</TableCell>
                      <TableCell align="right">IGST</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewingOrder.items?.length ? viewingOrder.items.map((item, index) => {
                      const product = item.product_details
                        || products.find(p => p.id === parseInt(item.product));
                      const productName = product?.name || 'N/A';
                      const uom = item.product_details?.uom_symbol || getProductUOM(product);
                      return (
                        <TableRow key={item.id || index}>
                          <TableCell>{productName}</TableCell>
                          <TableCell>{uom || 'N/A'}</TableCell>
                          <TableCell align="right">
                            {parseFloat(item.quantity || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            ₹{parseFloat(item.unit_price || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            {parseFloat(item.gst_rate || 0).toFixed(2)}%
                          </TableCell>
                          <TableCell align="right">
                            ₹{parseFloat(item.cgst_amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            ₹{parseFloat(item.sgst_amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            ₹{parseFloat(item.igst_amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            ₹{parseFloat(item.total || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    }) : (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No order items found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Notes</Typography>
                  <Typography variant="body1">{viewingOrder.notes || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Subtotal</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" align="right">
                            ₹{parseFloat(viewingOrder.total_amount || 0).toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Tax</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" align="right">
                            ₹{parseFloat(viewingOrder.tax_amount || 0).toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}><Divider /></Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Net Amount
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle1" fontWeight="bold"
                            color="primary" align="right">
                            ₹{parseFloat(viewingOrder.net_amount || 0).toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenViewDialog(false); setViewingOrder(null); }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {loading && (
        <Box sx={{
          position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: theme => theme.zIndex.modal + 1
        }}>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={24} />
            <Typography variant="body2">Loading sales data...</Typography>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default Sales;